import { AxiosResponse } from "axios";
import { getCurrentSessionIdToken } from "../queries/AuthToken";

export const getHeaderToken = async () => {
    const token = await getCurrentSessionIdToken();

    return {
        "Content-Type": "application/json",
        "Authorization": 'Bearer ' + token
    }
}

/**
 * Headers for the internal API, which needs *both* credentials: the bearer ID
 * token and the API key. A missing bearer header 401s at the gateway. Kept in
 * one place so each service can't drift into sending only one of the two.
 */
export const internalApiHeaders = async () => ({
    ...(await getHeaderToken()),
    "x-api-key": process.env.REACT_APP_IDWAL_INTERNAL_API_KEY
});

/**
 * Build an inspection-API URL, refusing to fall back to a relative path.
 *
 * An unresolved REACT_APP_INSPECTION_API is an empty string, which makes every
 * call relative: the request lands on the app's own origin, CloudFront answers
 * the SPA fallback with index.html and a 200, and the caller then reads HTML as
 * if it were an API response. Throwing here turns a whole class of silent
 * "nothing came back" bugs into one obvious error.
 */
export const inspectionApiUrl = (path: string): string => {
    const base = process.env.REACT_APP_INSPECTION_API;

    if (!base) {
        throw new Error(
            "REACT_APP_INSPECTION_API is not set — the build's .env is missing the inspection API URL (regenerate it with config.sh)"
        );
    }

    return base + path;
};

/**
 * Guard against a 200 that isn't the JSON we asked for — the SPA fallback, a
 * proxy error page, a login redirect. axios leaves a non-JSON body as a string,
 * so anything that isn't an object means we never reached the API, which is a
 * very different thing from the record not existing.
 */
export const assertJsonResponse = (response: AxiosResponse, url: string): void => {
    if (typeof response.data !== "object" || response.data === null) {
        throw new Error(
            `Expected JSON from ${url} but got ${typeof response.data} — the request probably never reached the API`
        );
    }
};